import {
  DatetimeRangeInput,
  IntervalInput,
  PageType,
} from "@/graphql/operations";
import { MimeType } from "@/util/MimeType";

export const PRESENTATION_ANALYTICS_VIEWS = ["performance", "pages"] as const;

export type PresentationAnalyticsView =
  (typeof PRESENTATION_ANALYTICS_VIEWS)[number];

export const PRESENTATION_ANALYTICS_PRESETS = [
  "30d",
  "90d",
  "ytd",
  "12m",
  "custom",
] as const;

export type PresentationAnalyticsPreset =
  (typeof PRESENTATION_ANALYTICS_PRESETS)[number];

export interface PresentationAnalyticsAccessInput {
  hasCurrentAccessResult: boolean;
  hasPresentationAnalytics: boolean;
  mayAdministrateRoot: boolean;
  deletedAt?: unknown | null;
  mimeType?: string | null;
  numNormalPages?: number | null;
}

export interface PresentationAnalyticsRange {
  from: Date;
  to: Date;
  preset: PresentationAnalyticsPreset;
}

export interface AnalyticsPageLike {
  id: string;
  pageNumber: number;
  pageType?: PageType | null;
  crossRevId?: string | null;
}

export interface AnalyticsHeatmapNodeLike {
  pageNumber?: number | null;
  crossRevId?: string | null;
  nVisits?: number | string | null;
  avgDuration?: number | string | null;
}

export interface PagePerformanceRow {
  pageId: string;
  pageNumber: number;
  crossRevId?: string | null;
  views: number;
  avgDurationSeconds: number;
}

export type PageSortKey = "pageNumber" | "views" | "avgDuration";
export type SortDirection = "asc" | "desc";

export interface AnalyticsViewsNodeLike {
  startsAt?: string | null;
  endsAt?: string | null;
  nViews?: number | string | null;
}

export interface AnalyticsWatchTimeNodeLike {
  startsAt?: string | null;
  endsAt?: string | null;
  avgWatchTimeInSeconds?: number | string | null;
}

export interface PerformanceBucket {
  startsAt: string;
  endsAt: string;
  views: number;
  avgWatchTimeInSeconds: number;
}

export interface PerformanceSummary {
  totalViews: number;
  weightedAvgWatchTimeInSeconds: number;
  activePages: number;
  totalPages: number;
}

export function hasPresentationAnalyticsAccess(
  input: PresentationAnalyticsAccessInput,
): boolean {
  return (
    input.hasCurrentAccessResult &&
    input.hasPresentationAnalytics &&
    input.mayAdministrateRoot &&
    input.deletedAt == null &&
    Boolean(input.numNormalPages && input.numNormalPages > 0) &&
    !MimeType.isVideo(input.mimeType || "")
  );
}

export function normalizeQueryValue(value: unknown): string | null {
  if (Array.isArray(value)) {
    return value[0] ? String(value[0]) : null;
  }

  return typeof value === "string" ? value : null;
}

export function resolvePresentationAnalyticsView(
  value: unknown,
): PresentationAnalyticsView {
  return normalizeQueryValue(value) === "pages" ? "pages" : "performance";
}

export function resolvePresentationAnalyticsPreset(
  value: unknown,
): PresentationAnalyticsPreset {
  const normalized = normalizeQueryValue(value);

  if (
    normalized &&
    PRESENTATION_ANALYTICS_PRESETS.includes(
      normalized as PresentationAnalyticsPreset,
    )
  ) {
    return normalized as PresentationAnalyticsPreset;
  }

  return "90d";
}

export function formatDateQueryValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseDateQueryValue(value: unknown): Date | null {
  const normalized = normalizeQueryValue(value);
  if (!normalized || !/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return null;
  }

  const [year, month, day] = normalized.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);

  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return parsed;
}

export function resolvePresentationAnalyticsRange(input: {
  preset?: unknown;
  from?: unknown;
  to?: unknown;
  now?: Date;
}): PresentationAnalyticsRange {
  const now = input.now ? new Date(input.now) : new Date();
  const today = startOfDay(now);
  const preset = resolvePresentationAnalyticsPreset(input.preset);

  if (preset === "custom") {
    const from = parseDateQueryValue(input.from);
    const to = parseDateQueryValue(input.to);

    if (from && to && from <= to) {
      return {
        from: startOfDay(from),
        to: startOfDay(to),
        preset: "custom",
      };
    }
  }

  if (preset === "30d") {
    return {
      from: addDays(today, -29),
      to: today,
      preset,
    };
  }

  if (preset === "ytd") {
    return {
      from: new Date(today.getFullYear(), 0, 1),
      to: today,
      preset,
    };
  }

  if (preset === "12m") {
    return {
      from: addMonths(today, -11),
      to: today,
      preset,
    };
  }

  return {
    from: addDays(today, -89),
    to: today,
    preset: preset === "custom" ? "90d" : preset,
  };
}

export function buildDatetimeRangeInput(
  range: PresentationAnalyticsRange,
): DatetimeRangeInput {
  return {
    start: {
      value: startOfDay(range.from).toISOString(),
      inclusive: true,
    },
    end: {
      value: addDays(startOfDay(range.to), 1).toISOString(),
      inclusive: false,
    },
  };
}

export function getPresentationAnalyticsStepInput(
  range: PresentationAnalyticsRange,
): IntervalInput {
  const days = getInclusiveRangeDays(range);

  if (days <= 45) {
    return { days: 1 };
  }

  if (days <= 180) {
    return { days: 7 };
  }

  return { months: 1 };
}

export function getInclusiveRangeDays(
  range: Pick<PresentationAnalyticsRange, "from" | "to">,
): number {
  const from = startOfDay(range.from);
  const to = startOfDay(range.to);
  const diff = to.getTime() - from.getTime();
  return Math.floor(diff / 86_400_000) + 1;
}

export function buildPagePerformanceRows(
  pages: AnalyticsPageLike[],
  stats: AnalyticsHeatmapNodeLike[],
): PagePerformanceRow[] {
  const statsByCrossRevId = new Map<string, AnalyticsHeatmapNodeLike>();
  const statsByPageNumber = new Map<number, AnalyticsHeatmapNodeLike>();

  for (const stat of stats) {
    if (stat.crossRevId) {
      statsByCrossRevId.set(stat.crossRevId, stat);
    }
    if (typeof stat.pageNumber === "number") {
      statsByPageNumber.set(stat.pageNumber, stat);
    }
  }

  return pages
    .filter((page) => (page.pageType || PageType.Normal) === PageType.Normal)
    .sort((left, right) => left.pageNumber - right.pageNumber)
    .map((page) => {
      const stat =
        (page.crossRevId
          ? statsByCrossRevId.get(page.crossRevId)
          : undefined) || statsByPageNumber.get(page.pageNumber);

      return {
        pageId: page.id,
        pageNumber: page.pageNumber,
        crossRevId: page.crossRevId,
        views: coerceNumber(stat?.nVisits),
        avgDurationSeconds: coerceNumber(stat?.avgDuration),
      };
    });
}

export function sortPagePerformanceRows(
  rows: PagePerformanceRow[],
  sortKey: PageSortKey,
  sortDirection: SortDirection,
): PagePerformanceRow[] {
  const sortedRows = [...rows];
  const multiplier = sortDirection === "asc" ? 1 : -1;

  sortedRows.sort((left, right) => {
    if (sortKey === "views") {
      return (
        multiplier *
        compareNumbers(
          left.views,
          right.views,
          left.pageNumber,
          right.pageNumber,
        )
      );
    }

    if (sortKey === "avgDuration") {
      return (
        multiplier *
        compareNumbers(
          left.avgDurationSeconds,
          right.avgDurationSeconds,
          left.pageNumber,
          right.pageNumber,
        )
      );
    }

    return multiplier * compareNumbers(left.pageNumber, right.pageNumber);
  });

  return sortedRows;
}

export function getPaginationPageCount(
  totalItems: number,
  pageSize: number,
): number {
  if (pageSize <= 0) {
    return 1;
  }

  return Math.max(Math.ceil(totalItems / pageSize), 1);
}

export function paginateItems<T>(
  items: T[],
  currentPage: number,
  pageSize: number,
): T[] {
  const pageCount = getPaginationPageCount(items.length, pageSize);
  const safePage = Math.min(Math.max(currentPage, 1), pageCount);
  const startIndex = (safePage - 1) * pageSize;
  return items.slice(startIndex, startIndex + pageSize);
}

export function buildPaginationItems(
  totalPages: number,
  currentPage: number,
): Array<number | "ellipsis"> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages]);

  for (
    let pageNumber = Math.max(currentPage - 1, 2);
    pageNumber <= Math.min(currentPage + 1, totalPages - 1);
    pageNumber += 1
  ) {
    pages.add(pageNumber);
  }

  const sortedPages = [...pages].sort((left, right) => left - right);
  const items: Array<number | "ellipsis"> = [];

  for (const pageNumber of sortedPages) {
    const previousPageNumber = items[items.length - 1];

    if (
      typeof previousPageNumber === "number" &&
      pageNumber - previousPageNumber > 1
    ) {
      items.push("ellipsis");
    }

    items.push(pageNumber);
  }

  return items;
}

export function buildPerformanceBuckets(
  views: AnalyticsViewsNodeLike[],
  watchTimes: AnalyticsWatchTimeNodeLike[],
): PerformanceBucket[] {
  const buckets = new Map<string, PerformanceBucket>();

  for (const bucket of views) {
    if (!bucket.startsAt || !bucket.endsAt) {
      continue;
    }

    buckets.set(bucket.startsAt, {
      startsAt: bucket.startsAt,
      endsAt: bucket.endsAt,
      views: coerceNumber(bucket.nViews),
      avgWatchTimeInSeconds: 0,
    });
  }

  for (const bucket of watchTimes) {
    if (!bucket.startsAt || !bucket.endsAt) {
      continue;
    }

    const existing = buckets.get(bucket.startsAt);

    buckets.set(bucket.startsAt, {
      startsAt: bucket.startsAt,
      endsAt: bucket.endsAt,
      views: existing?.views || 0,
      avgWatchTimeInSeconds: coerceNumber(bucket.avgWatchTimeInSeconds),
    });
  }

  return [...buckets.values()].sort((left, right) =>
    left.startsAt.localeCompare(right.startsAt),
  );
}

export function summarizePerformance(
  buckets: PerformanceBucket[],
  pageRows: PagePerformanceRow[],
): PerformanceSummary {
  const totalViews = buckets.reduce((sum, bucket) => sum + bucket.views, 0);
  const weightedWatchTime = buckets.reduce(
    (sum, bucket) => sum + bucket.avgWatchTimeInSeconds * bucket.views,
    0,
  );

  return {
    totalViews,
    weightedAvgWatchTimeInSeconds:
      totalViews > 0 ? weightedWatchTime / totalViews : 0,
    activePages: pageRows.filter((row) => row.views > 0).length,
    totalPages: pageRows.length,
  };
}

function coerceNumber(value: number | string | null | undefined): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

function compareNumbers(
  left: number,
  right: number,
  tieBreakerLeft?: number,
  tieBreakerRight?: number,
) {
  if (
    left === right &&
    tieBreakerLeft !== undefined &&
    tieBreakerRight !== undefined
  ) {
    return tieBreakerLeft - tieBreakerRight;
  }

  return left - right;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return startOfDay(result);
}

function addMonths(date: Date, months: number): Date {
  const targetMonthIndex = date.getMonth() + months;
  const targetMonthStart = new Date(date.getFullYear(), targetMonthIndex, 1);
  const lastDayOfTargetMonth = new Date(
    targetMonthStart.getFullYear(),
    targetMonthStart.getMonth() + 1,
    0,
  ).getDate();

  return new Date(
    targetMonthStart.getFullYear(),
    targetMonthStart.getMonth(),
    Math.min(date.getDate(), lastDayOfTargetMonth),
  );
}
